import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useForm as useFormV7, useFormState } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { EmployeeFormPageProps, EmployeeFormData, Breadcrumb } from '@personnel/presentation/types';
import { PageLayout } from './PageLayout';
import { SecondaryHeader } from './SecondaryHeader';
import { Breadcrumbs } from './Breadcrumbs';
import { EmployeeForm } from '@personnel/presentation/components/form';
import {
  employeeDetailDtoToViewModel,
  viewModelToEmployeeFormDefaults,
} from '@personnel/presentation/mappers/employeeViewModelMapper';
import {
  buildNotificationPreferencesFormValue,
  notificationPreferencesFormValueToDto,
} from '@personnel/presentation/mappers/notificationPreferencesMapper';
import { toast } from 'sonner';
import {
  hasRoleRequiredPermissions,
  PERMISSIONS_THAT_REQUIRE_CREDENTIALS,
  PERMISSIONS_THAT_REQUIRE_PIN,
} from '@personnel/presentation/components/form/EmployeeForm/utils';
import { useEmailVerificationBadge } from '@personnel/presentation/components/form/EmployeeForm/hooks';

import {
  usePersonnelEmployee,
  usePersonnelMutations,
  useApiErrorHandler,
  useFormErrorHandler,
  usePersonnelProfiles,
  usePersonnelStores,
  useNotificationPreferencesConfig,
  useRoleList,
} from '@personnel/presentation/hooks';
import { hasUserChanges, useEmployeeSaveIntent } from '@personnel/presentation/hooks/useEmployeeSaveIntent';
import { DEFAULT_FILTERS, buildEmployeeFiltersSearch } from '@personnel/presentation/hooks/useEmployeeFilters';
import { getPersonnelStorage } from '@personnel/composition';
import { EMPLOYEE_FORM_FIELD_MAPPINGS } from '@personnel/domain/errors';
import { EmployeeSaveIntents, VerificationStatus } from '@personnel/domain/types';
import { useEmployeeFormSchema } from '@personnel/presentation/components/form/EmployeeForm';

/** The toast that closes each save. The three are the same write and only their wording differs. */
const SUCCESS_MESSAGE_KEYS = {
  [EmployeeSaveIntents.SAVE]: 'form.response.editUserSuccess',
  [EmployeeSaveIntents.SAVE_AND_INVITE]: 'form.response.saveAndInviteSuccess',
  [EmployeeSaveIntents.RESEND_LINK]: 'form.response.resendLinkSuccess',
};

const EMPTY_FORM_DEFAULTS: EmployeeFormData = {
  scope: 'general',
  roleUuid: '',
  profileId: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  posPin: '',
  isActive: true,
  assignedStores: [],
  allStoresAssigned: false,
  notificationPreferences: {},
  _credentialsEnabled: false,
  _pinEnabled: false,
  _isEditMode: false,
};

export const EmployeeFormPage = ({ mode }: EmployeeFormPageProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const { handleError } = useApiErrorHandler();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { employeeUuid } = useParams<{ employeeUuid: string }>();
  const employeeId = mode === 'edit' ? employeeUuid : undefined;

  // Use domain hooks - no repository creation here!
  const detailRequest = employeeId ? { userUuid: employeeId, includeAssignedStores: true } : undefined;

  const { data: detail, isError, error } = usePersonnelEmployee(detailRequest);
  const { createEmployee, updateEmployee } = usePersonnelMutations();
  const { data: profiles } = usePersonnelProfiles();
  const { data: stores } = usePersonnelStores();
  const { data: notificationConfig, isLoading: isLoadingNotificationConfig } = useNotificationPreferencesConfig();
  const { roles } = useRoleList();

  if (isError && error) {
    handleError(error);
  }

  const isEditMode = mode === 'edit';
  const areFormDependenciesLoaded = profiles && profiles.length > 0 && roles.length > 0 && stores && stores.length > 0;
  const isLoadingEditData = isEditMode && (!detail || !areFormDependenciesLoaded);

  /**
   * Empty until the configuration answers, and empty for good if it failed. An empty matrix paints
   * no notification row and sends no `notificationPreferences`, which on this endpoint means the
   * groups keep whatever they had.
   */
  const notificationGroups = useMemo(() => notificationConfig ?? [], [notificationConfig]);

  const editFormDefaults = useCallback((): EmployeeFormData | null => {
    if (!isEditMode || !detail) return null;

    const formDefaults = viewModelToEmployeeFormDefaults(employeeDetailDtoToViewModel(detail), notificationGroups);
    const selectedRole = roles.find((role) => role.uuid === formDefaults.roleUuid);

    return {
      ...formDefaults,
      _isEditMode: true,
      _credentialsEnabled: hasRoleRequiredPermissions(selectedRole, PERMISSIONS_THAT_REQUIRE_CREDENTIALS),
      _pinEnabled: hasRoleRequiredPermissions(selectedRole, PERMISSIONS_THAT_REQUIRE_PIN),
    };
  }, [detail, isEditMode, notificationGroups, roles]);

  const defaultValues = useMemo<EmployeeFormData>(
    () =>
      editFormDefaults() ?? {
        ...EMPTY_FORM_DEFAULTS,
        notificationPreferences: buildNotificationPreferencesFormValue(notificationGroups),
      },
    [editFormDefaults, notificationGroups],
  );

  const { resolver } = useEmployeeFormSchema();
  const methods = useFormV7<EmployeeFormData>({
    defaultValues,
    resolver: resolver as any,
    mode: 'onBlur',
  });

  const hasResetWithDetailRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEditMode || !detail || !areFormDependenciesLoaded || isLoadingNotificationConfig) return;
    if (hasResetWithDetailRef.current === detail.uuid) return;

    const formValues = editFormDefaults();
    if (!formValues) return;

    methods.reset(formValues);
    hasResetWithDetailRef.current = detail.uuid;
  }, [areFormDependenciesLoaded, detail, editFormDefaults, isEditMode, isLoadingNotificationConfig, methods]);

  const { dirtyFields } = useFormState({ control: methods.control });
  const emailVerificationStatus = useEmailVerificationBadge(methods.control);
  const saveAction = useEmployeeSaveIntent({
    isEditMode,
    hasChanges: hasUserChanges(dirtyFields),
    verificationStatus: emailVerificationStatus,
  });

  const { handleFormError } = useFormErrorHandler<EmployeeFormData>(EMPLOYEE_FORM_FIELD_MAPPINGS);

  /**
   * The matrix as the write carries it, or nothing at all.
   *
   * Sending an empty list would switch every group off, so a screen that painted no row omits the
   * key instead and lets the backend keep, or default, what it already has.
   */
  const notificationPreferencesPayload = useCallback(
    (data: EmployeeFormData) => {
      if (notificationGroups.length === 0) return undefined;
      return notificationPreferencesFormValueToDto(notificationGroups, data.notificationPreferences);
    },
    [notificationGroups],
  );

  const handleSubmit = useCallback(
    async (data: EmployeeFormData) => {
      setIsSubmitting(true);
      try {
        if (mode === 'edit' && employeeId) {
          await updateEmployee({
            userUuid: employeeId,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email && data.email.trim() !== '' ? data.email : undefined,
            posPin: data.posPin ?? undefined,
            profileId: data.profileId ?? '',
            roleUuid: data.roleUuid,
            assignedStores: (data.assignedStores || []).map((s) => s.uuid),
            allStoresAssigned: !!data.allStoresAssigned,
            notificationPreferences: notificationPreferencesPayload(data),
            intent: saveAction.intent,
            emailChanged: data.email.trim().toLowerCase() !== (data._initialEmail ?? '').trim().toLowerCase(),
            roleChanged: !!dirtyFields.roleUuid,
          });
          toast.success(t(SUCCESS_MESSAGE_KEYS[saveAction.intent]), { duration: 3000 });
        } else {
          await createEmployee({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            profileId: data.profileId ?? '',
            posPin: data.posPin ?? '',
            roleUuid: data.roleUuid,
            assignedStores: (data.assignedStores || []).map((s) => s.uuid),
            allStoresAssigned: !!data.allStoresAssigned,
            notificationPreferences: notificationPreferencesPayload(data),
          });
          toast.success(t('form.response.createUserSuccess'), { duration: 3000 });
        }
      } catch (error) {
        const result = handleFormError(error, methods.setError);

        if (result.handled) {
          const errorMessage = mode === 'edit' ? t('form.errors.updateError') : t('form.errors.createError');
          toast.error(errorMessage, { duration: 6000 });
        } else {
          handleError(error);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      mode,
      employeeId,
      updateEmployee,
      createEmployee,
      notificationPreferencesPayload,
      saveAction.intent,
      dirtyFields.roleUuid,
    ],
  );

  // Return to the employees list preserving the last-used filters (persisted in
  // storage), so the breadcrumb lands on the filtered list rather than a bare one.
  const employeesListPath = useMemo(() => {
    const storedFilters = getPersonnelStorage().loadEmployeeFilters(DEFAULT_FILTERS);
    const search = buildEmployeeFiltersSearch(storedFilters);
    return search ? `/employees?${search}` : '/employees';
  }, []);

  // The heading of an edit is the person, not the action: this screen is where their access gets
  // consulted as much as changed.
  const editTitle: string = detail?.fullName || t('edit.title');

  const config: {
    title: string;
    description: string;
    breadcrumbs: Breadcrumb[];
  } = {
    create: {
      title: t('create.title'),
      description: t('create.description'),
      breadcrumbs: [{ label: t('list.title'), path: employeesListPath }, { label: t('create.title') }],
    },
    edit: {
      title: editTitle,
      description: t('edit.description'),
      breadcrumbs: [{ label: t('list.title'), path: employeesListPath }, { label: editTitle }],
    },
    duplicate: {
      title: t('duplicate.title'),
      description: t('create.description'),
      breadcrumbs: [{ label: t('list.title'), path: employeesListPath }, { label: t('duplicate.title') }],
    },
  }[mode];

  if (isLoadingEditData || isLoadingNotificationConfig) {
    return (
      <PageLayout>
        <Breadcrumbs items={config.breadcrumbs} />
        <SecondaryHeader description={config.description} title={config.title} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Breadcrumbs items={config.breadcrumbs} />
      <SecondaryHeader description={config.description} title={config.title} />
      <EmployeeForm
        cancelPath={employeesListPath}
        form={methods}
        isAccountInactive={detail?.verificationStatus === VerificationStatus.NOT_VERIFIED}
        isEditMode={isEditMode}
        isSubmitting={isSubmitting}
        key={employeeId || 'create'}
        notificationConfig={notificationGroups}
        onSubmit={handleSubmit}
        saveAction={saveAction}
      />
    </PageLayout>
  );
};
